/**
 * Projection UI de l'écriture Hector pour UNE session documentaire.
 *
 * Une projection globale historique ne peut pas dire
 * quelle conversation est réellement modifiée : plusieurs résidents peuvent
 * écrire en parallèle. Les compteurs `writer` et `mutation` du registre sont
 * donc le seul fait lu par les indicateurs et le bandeau Canvas.
 */

import * as React from 'react';
import { canvasDocumentRuntime } from './canvasDocumentRuntime';
import type { CanvasSessionSnapshot } from './canvasSessionRegistry';

/** L'agent modifie-t-il effectivement ce résident, sans repli visuel global ? */
export function isCanvasSessionWritingByAgent(
  session: Pick<CanvasSessionSnapshot, 'streamCount' | 'writerCount' | 'mutationCount'> | null,
): boolean {
  // Un bloc SSE n'emprunte pas `canvas_perform_action`, mais il insère bien du
  // contenu dans CE SuperDoc. L'ignorer rendait le Canvas silencieux pendant
  // une rédaction générée : aucun bandeau ni arrêt alors que le stream exact
  // était actif (#1282). Les trois compteurs restent scellés à la session.
  return Boolean(
    session
    && (session.streamCount > 0 || session.writerCount > 0 || session.mutationCount > 0),
  );
}

/** Lecture impérative pour les chemins hors React. */
export function readCanvasSessionWritingByAgent(conversationId: string): boolean {
  const session = canvasDocumentRuntime.getSessionsSnapshot().sessions.find(
    (candidate) => candidate.conversationId === conversationId,
  ) ?? null;
  return isCanvasSessionWritingByAgent(session);
}

/** Projection réactive du même résident : aucune autre conversation ne peut la modifier. */
export function useCanvasSessionWritingByAgent(conversationId: string): boolean {
  return React.useSyncExternalStore(
    (notify) => canvasDocumentRuntime.subscribeSessions(() => notify()),
    () => readCanvasSessionWritingByAgent(conversationId),
    () => false,
  );
}
